<template>
	<Form ref="formRef" :validation-schema="validationSchema" class="form-container" @submit="onSubmit" v-slot="{ errors }">
		<div v-for="field in fields" :key="field.name" :class="['form-input', field.width]">
			<label :for="field.name">{{ field.label }}</label>
			<Field
				v-if="field.type === 'select'"
				as="select"
				:id="field.name"
				:name="field.name"
				v-model="formData[field.name]"
				:disabled="isFieldDisabled(field)"
				@update:model-value="handleFieldChange(field, $event)"
			>
				<option></option>
				<option v-for="option in field.options" :key="option.id" :value="option.id">
					{{ option.name }}
				</option>
			</Field>
			<Field
				v-else
				:id="field.name"
				:name="field.name"
				:type="field.type"
				v-model="formData[field.name]"
				:disabled="isFieldDisabled(field)"
				@update:model-value="handleFieldChange(field, $event)"
			/>
			<ErrorMessage v-show="mode !== 'view'" :name="field.name" class="error" />
		</div>

		<button
			v-if="mode !== 'view'"
			class="submit-button col-12"
			type="submit"
			:disabled="!isFormValid(errors) || isSubmitting"
		>
			Submit
		</button>
	</Form>
</template>

<script setup lang="ts">
	import { defineProps, reactive, watchEffect, computed, ref } from 'vue';
	import { Form, Field, ErrorMessage } from 'vee-validate';
	import { Schema } from 'yup';
	import { ServiceType, FormField, DisbursementStatuses } from '@/models/loans';
	import { getMaskingStrategy, formatWithCursorTracking } from '@/composables/maskingUtils';
	import useFormatter from '@/use/formatter';
	import useFunctions from '@/use/functions1';

	const { convertMoneyStringToNumber } = useFunctions();
	const { formatDateInputDisplayValue, formatMoney } = useFormatter();

	const props = defineProps<{
		onSubmit: (formData: Record<string, any>) => Promise<void>;
		isSubmitting: boolean;
		fields: Array<FormField>;
		validationSchema: Schema;
		mode: 'edit' | 'view' | 'add';
		initialValues?: Record<string, any> | null;
	}>();

	const formRef = ref<InstanceType<typeof Form> | null>(null);

	const formData = reactive<Record<string, any>>({});

	const initialValuesProp = computed(() => {
		return mapDisbursementObject(props.initialValues)
	});

	const isFormValid = computed(() => {
		return (errors: Partial<Record<string, string | undefined>>) => errors && Object.keys(errors).length === 0;
	});

	function getField(name: string) : FormField | undefined {
		return props.fields.find(f => f.name == name);
	}

	function mapDisbursementObject(selectedDisbursement: any) {
 		const disbursementStatus = getField("disbursementStatusTypeId")?.options?.find((o: ServiceType) => o.id === selectedDisbursement?.disbursementStatusTypeId);
 		const drawDisbursementStatusReasonTypeId =  getField("drawDisbursementStatusReasonTypeId")?.options?.find((o: ServiceType) => o.id === selectedDisbursement?.drawDisbursementStatusReasonTypeId);
		const disbursementDateDisplay = formatDateInputDisplayValue(selectedDisbursement.transactionDate);
		
		return {
			...selectedDisbursement,
			disbursementStatusTypeName: disbursementStatus?.name,
			drawDisbursementStatusReasonTypeName: drawDisbursementStatusReasonTypeId?.name,
			disbursementDateDisplay: disbursementDateDisplay,
		}
	}

	function handleFieldChange(field: FormField, value: any) {
		switch (field.name) 
		{ 
			case "disbursementStatusTypeId": 
				formData.disbursementStatusTypeName = field?.options?.find((o: ServiceType) => o.id === Number(value))?.name;

				if (value === DisbursementStatuses.PreDisbursementCancellation) {
					formData.disbursedAmount = 0;
					formData.disbursedAmountFormatted = formatMoney(0);
				}
				break;
			case "drawDisbursementStatusReasonTypeId":
				formData.drawDisbursementStatusReasonTypeName = field?.options?.find((o: ServiceType) => o.id === Number(value))?.name;
				break;
			case "disbursedAmount":
				if (value === '' || value === null || value === undefined) {
					formData.disbursedAmount = undefined;
					formData.disbursedAmountFormatted = undefined;
				} else {
					formData.disbursedAmountFormatted = formatMoney(typeof value === 'string' ? convertMoneyStringToNumber(value) : value);
				}
				break;
			case "certifiedAmount":
				formData.certifiedAmount = typeof value === 'string' ? convertMoneyStringToNumber(value) : value;
				formData.certifiedAmountFormatted = formatMoney(typeof value === 'string' ? convertMoneyStringToNumber(value) : value);
				break;
			case "transactionDate":
				formData.disbursementDateDisplay = formatDateInputDisplayValue(value);
				break;
			default:
				break;
		}
	}

	function handleInputWithMasking(event: InputEvent, fieldName: keyof typeof formData) {
		const input = event.target as HTMLInputElement;
		formData[fieldName] = formatWithCursorTracking(input);
	}

	function isFieldDisabled(field: FormField) {
		const disabledOnEdit = props.mode === 'edit' && field.disabledOnEdit === true;
		const disabledOnAdd = props.mode === 'add' && field.disabledOnAdd === true;

		return props.mode === 'view' || props.isSubmitting || disabledOnEdit || disabledOnAdd;
	}


	async function onSubmit() {
		if (props.onSubmit) {
			await props.onSubmit({ ...formData });
		}
	}

	watchEffect(() => {
		props.fields.forEach((field) => {
			formData[field.name] = field.value;
			handleFieldChange(field, field.value);
		});
	});

	watchEffect(() => {
		props.fields.forEach((field) => {
			if (field.maskType && formData[field.name] !== undefined) {
				const strategy = getMaskingStrategy(field.maskType);
				const rawValue = String(formData[field.name]);				
				const maskedValue = strategy(rawValue);

				formData[field.name] = maskedValue;
			}
		});
	});

	defineExpose({
		formData,
		initialValuesProp,
		validate: () => formRef.value?.validate()
	})
</script>

<style scoped src="@/assets/css/loans/form.css"></style>
